import { useMemo } from 'react';
import _ from 'lodash';

import useCachedDataFetcher from '~/components/hooks/useCachedDataFetcher';
import type { PossibleAdjustersResponsePossibleAdjuster } from '~/server_shared/openapi/data-contracts';

export interface AdjusterUser extends PossibleAdjustersResponsePossibleAdjuster {
  role_type: string;
}

interface IFetchAdjusters {
  isLoadingPossibleAdjusters: boolean;
  isErrorPossibleAdjusters: boolean;
  possibleAdjustersWithRemoved: AdjusterUser[];
  possibleAdjustersWithDaSpecialist: AdjusterUser[];
  possibleAdjustersWithoutDaSpecialist: AdjusterUser[];
}

export default function useFetchAdjusters(organizationId: string | number, shouldFetch?: boolean): IFetchAdjusters {
  const {
    isLoading,
    isError,
    data: possibleAdjustersWithLicenses,
  } = useCachedDataFetcher<AdjusterUser[]>({
    url: `/api/v1/organizations/${organizationId}/possible_adjusters_with_licenses`,
    shouldUseCache: true,
    localCacheKey: `possible_adjusters_with_licenses_${organizationId}`,
    shouldFetch,
  });

  const { possibleAdjustersWithRemoved, possibleAdjustersWithDaSpecialist, possibleAdjustersWithoutDaSpecialist } =
    useMemo(() => {
      const possibleAdjustersWithRemoved = _.orderBy(possibleAdjustersWithLicenses, ['username']);
      const possibleAdjustersWithDaSpecialist = possibleAdjustersWithRemoved.filter((adjuster) => !adjuster.is_removed);

      const possibleAdjustersWithoutDaSpecialist = possibleAdjustersWithDaSpecialist.filter(
        (adjuster) => adjuster.role_type === 'claim_rep'
      );
      return {
        possibleAdjustersWithRemoved,
        possibleAdjustersWithDaSpecialist,
        possibleAdjustersWithoutDaSpecialist,
      };
    }, [possibleAdjustersWithLicenses]);

  return {
    isLoadingPossibleAdjusters: isLoading,
    isErrorPossibleAdjusters: isError,
    possibleAdjustersWithRemoved,
    possibleAdjustersWithDaSpecialist,
    possibleAdjustersWithoutDaSpecialist,
  };
}
